import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applicationApi, documentApi, schoolApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Banner,
  Button,
  Card,
  Empty,
  Grid2,
  Modal,
  Muted,
  Spinner,
  Steps,
  Tag,
  fmtDate,
  useAsync
} from '../../ui.jsx';

const STAGES = ['Application', 'Programmes', 'Answers', 'Documents', 'Review'];

// The backend refuses more than four, so the picker must not offer a fifth.
const MAX_PROGRAMMES = 4;

// Same four as the master profile, so the preview names them the way the
// student named them when uploading.
const COMMON_DOCUMENT_LABELS = {
  photo: 'Passport photograph',
  ghanaCard: 'Ghana Card',
  resultSlip: 'WASSCE result slip',
  referenceLetter: 'Reference letter',
};

export default function Apply() {
  const navigate = useNavigate();
  const notice = useNotice();
  const location = useLocation();
  const { schoolSlug, programmeId, programmeIds } = location.state || {};

  const list = useAsync(() => applicationApi.list(), []);
  const [activeId, setActiveId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState('');

  const applications = list.data?.applications || [];
  const draft = applications.find((a) => a.status === 'draft') || null;
  const active = activeId ? applications.find((a) => a.id === activeId) : draft;
  const school = active?.school;

  // `review` is the endpoint that returns the master profile, the fee quote and
  // the missing document and answer lists the editor needs, so it is the only
  // call that can drive this screen.
  const detail = useAsync(
    () => (activeId ? applicationApi.review(activeId) : Promise.resolve(null)),
    [activeId]
  );

  /**
   * The card badge and the editor used to disagree: the badge counted the
   * programmes in the list response, fetched once on mount, while the editor
   * counted the ones in the review response. Saving four choices updated the
   * editor and left the badge on the count it was first handed, so one
   * application read "4 of 4 chosen" and "2 programmes" at the same time.
   *
   * The open card now counts from the review payload the editor is already
   * showing, and the list is refetched whenever that count moves, so the two
   * cannot drift apart again.
   */
  const liveCount = detail.data?.application?.programmes?.length ?? null;
  const [knownCount, setKnownCount] = useState(null);

  useEffect(() => {
    if (liveCount === null) return;
    setKnownCount((previous) => {
      if (previous !== null && previous !== liveCount) list.reload();
      return liveCount;
    });
  }, [liveCount]);

  const programmeCount = (item) =>
    item.id === activeId && liveCount !== null ? liveCount : item.programmes.length;

  useEffect(() => {
    if (!detail.data?.application) return;
    const existing = {};
    for (const answer of detail.data.application.answers || []) existing[answer.key] = answer.value;
    setAnswers(existing);
  }, [detail.data]);

  /** Starting a school application needs the school id, which the slug page does not carry. */
  const startFromSlug = useAsync(async () => {
    if (!schoolSlug || draft) return null;
    const catalogue = await schoolApi.detail(schoolSlug);
    const started = await applicationApi.start({
      schoolId: catalogue.school.id,
      programmeId,
      // The school page can rank up to four; the route replaces the draft's list
      // with exactly these, in the order they were ticked.
      programmeIds: Array.isArray(programmeIds) ? programmeIds : []
    });
    return started;
  }, [schoolSlug, draft]);

  useEffect(() => {
    if (startFromSlug.data?.application) {
      list.reload();
      setActiveId(startFromSlug.data.application.id);
    }
  }, [startFromSlug.data]);

  useEffect(() => {
    if (startFromSlug.error) notice.fromError(startFromSlug.error);
  }, [startFromSlug.error]);

  const refresh = async () => {
    await list.reload();
    if (activeId) await detail.reload();
  };

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await refresh();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy('');
    }
  };

  if (list.loading || startFromSlug.loading) return <Spinner label="Loading your applications" />;

  if (!applications.length) {
    return (
      <Empty
        title="No applications yet"
        action={
          <Button onClick={() => navigate('/schools')}>Browse schools</Button>
        }
      >
        Pick a programme and Entrance will carry your saved profile into the application. You apply to one
        school at a time.
      </Empty>
    );
  }

  return (
    <>
      <h2>Your applications</h2>
      <Muted>One school at a time. Your master profile carries over to each of them.</Muted>

      <Card>
        {applications.map((item) => {
            const count = programmeCount(item);
            return (
              <div className="prog" key={item.id}>
                <i className="ub">{item.school?.shortName}</i>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{item.school?.name}</b>
                  <div className="mu">
                    {item.reference} · {count} {count === 1 ? 'programme' : 'programmes'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Tag tone={item.status === 'draft' ? 'mid' : item.status === 'offered' ? 'ok' : item.status === 'rejected' ? 'no' : ''}>
                    {item.status.replace(/_/g, ' ')}
                  </Tag>
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" onClick={() => setActiveId(item.id)}>Open</Button>
                  </div>
                </div>
              </div>
            );
          })}
      </Card>

      {activeId && detail.loading ? <Spinner label="Loading application" /> : null}

      {activeId && detail.data?.application ? (
        <ApplicationEditor
          detail={detail.data}
          answers={answers}
          setAnswers={setAnswers}
          busy={busy}
          run={run}
          school={school}
          onDone={() => {
            setActiveId(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

function ApplicationEditor({ detail, answers, setAnswers, busy, run, onDone }) {
  const navigate = useNavigate();
  const notice = useNotice();
  const { application, masterProfile, quote, checklist } = detail;
  const school = application.school;

  const schoolQuestions = useAsync(
    () => (school?.slug ? schoolApi.detail(school.slug) : Promise.resolve(null)),
    [school?.slug]
  );
  const questionList = schoolQuestions.data?.school?.questions || [];
  const requiredDocs = schoolQuestions.data?.school?.documents || [];

  // Questions and required documents hang off the school; the programme
  // catalogue is a sibling of `school` in the response, not a field on it.
  const catalogue = schoolQuestions.data?.programmes || [];

  const [selected, setSelected] = useState(application.programmes.map((p) => p.id));
  const [showPreview, setShowPreview] = useState(false);

  // The server owns readiness. It also checks the master profile's four common
  // documents and the chosen programmes, which the client cannot judge on its
  // own, so recomputing it here would wrongly let an incomplete file be sent.
  const complete = Boolean(checklist?.ready);
  const missingDocuments = checklist?.missingDocuments || [];
  const missingAnswers = checklist?.missingAnswers || [];
  const missingProfile = checklist?.missingProfile || [];

  const toggleProgramme = (id) => {
    setSelected((list) =>
      list.includes(id) ? list.filter((x) => x !== id) : list.length >= MAX_PROGRAMMES ? list : [...list, id]
    );
  };

  const submit = async () => {
    if (selected.length === 0) {
      notice.error('Choose at least one programme.');
      return;
    }
    await run('programmes', async () => {
      // The route reads `programmeIds`, and order is the student's priority
      // order, so the array is sent as chosen.
      await applicationApi.chooseProgrammes(application.id, { programmeIds: selected });
      notice.success('Programmes saved.');
    });
  };

  const saveAnswers = async () => {
    await run('answers', async () => {
      await applicationApi.saveAnswers(application.id, { answers });
      notice.success('Answers saved.');
    });
  };

  const upload = async (event, key) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await run(`doc-${key}`, async () => {
      await applicationApi.uploadDocument(application.id, file, key);
      notice.success('Document uploaded.');
    });
  };

  const sendApplication = async () => {
    await run('submit', async () => {
      const sent = await applicationApi.submit(application.id);
      notice.success(sent.message);
      onDone();
    });
  };

  const step = !checklist?.programmes
    ? 1
    : !checklist?.answers
      ? 2
      : !checklist?.documents || !checklist?.masterProfile
        ? 3
        : 4;

  return (
    <>
      <h2 style={{ marginTop: 26 }}>{school.name}</h2>
      <Muted>
        Reference {application.reference}. Applications go to one school at a time.
      </Muted>

      <Card>
        <Steps current={step} items={STAGES} />
        <div className="steps" style={{ marginTop: -6 }}>
          {(application.timeline || []).map((item) => (
            <span key={item.stage} className={item.done ? 'dn' : ''}>
              <em>{item.stage}</em>
            </span>
          ))}
        </div>
      </Card>

      <Card>
        <div className="row sp">
          <h3 style={{ fontSize: 18, margin: 0 }}>Programme choices</h3>
          <Muted>{selected.length} of 4 chosen (in order of preference)</Muted>
        </div>
        {catalogue.length === 0 ? (
          <Muted>{school.name} has not published a programme list on Entrance yet.</Muted>
        ) : (
          catalogue.map((programme) => {
            const idx = selected.indexOf(programme.id);
            const choice = idx === -1 ? null : idx + 1;
            return (
              <label className="prog" key={programme.id} style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selected.includes(programme.id)}
                  onChange={() => toggleProgramme(programme.id)}
                  style={{ width: 'auto', margin: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <b>{programme.name}</b>
                  <div className="mu">
                    Cut-off {programme.cutOff ?? 'not set'}
                    {programme.cutOffEstimated ? ' (est.)' : ''} · {programme.fieldLabel}
                  </div>
                </div>
                {choice ? (
                  <Tag tone="ok">
                    {choice === 1 ? '1st' : choice === 2 ? '2nd' : choice === 3 ? '3rd' : '4th'} choice
                  </Tag>
                ) : null}
              </label>
            );
          })
        )}
        <div style={{ marginTop: 12 }}>
          <Button busy={busy === 'programmes'} onClick={submit}>Save programme choices</Button>
        </div>
      </Card>

      {questionList.length ? (
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Questions for {school.shortName}</h3>
          <Muted>These are this school's own questions. They are not shared with anyone else.</Muted>
          {questionList.map((q) => (
            <div key={q.key} style={{ marginTop: 12 }}>
              {q.type === 'select' ? (
                <label>
                  {q.label}
                  {q.required !== false ? <span style={{ color: 'var(--no)' }}> *</span> : null}
                  <select
                    value={answers[q.key] || ''}
                    onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                  >
                    <option value="">Choose</option>
                    {q.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  {q.label}
                  {q.required !== false ? <span style={{ color: 'var(--no)' }}> *</span> : null}
                  <input
                    value={answers[q.key] || ''}
                    onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                  />
                </label>
              )}
            </div>
          ))}
          <div style={{ marginTop: 14 }}>
            <Button busy={busy === 'answers'} onClick={saveAnswers}>Save answers</Button>
          </div>
        </Card>
      ) : null}

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Documents for {school.shortName}</h3>
        <Muted>
          Your four common documents are already on your profile. Anything this school asks for
          separately is uploaded here.
        </Muted>
        {requiredDocs.length === 0 ? (
          <Muted>{school.name} asks for no extra documents beyond the common four.</Muted>
        ) : (
          requiredDocs.map((doc) => {
            // The route scopes the upload to the school itself, so the client
            // sends the plain key rather than the stored composite kind.
            const kind = `school:${school.slug}:${doc.key}`;
            const uploaded = (application.documents || []).find((d) => d.kind === kind);
            return (
              <div className="prog" key={doc.key}>
                <div style={{ flex: 1 }}>
                  <b>{doc.label}</b>
                  <div className="mu">
                    {uploaded ? `Uploaded ${fmtDate(uploaded.createdAt)}` : doc.hint || 'Not uploaded'}
                  </div>
                </div>
                <label className="btn sm" style={{ margin: 0, cursor: 'pointer', display: 'inline-block' }}>
                  {busy === `doc-${kind}` ? 'Uploading...' : uploaded ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => upload(e, doc.key)}
                    disabled={busy === `doc-${kind}`}
                  />
                </label>
              </div>
            );
          })
        )}
      </Card>

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Review and submit</h3>
        <Grid2>
          <div>
            <p className="hd">From your master profile</p>
            {masterProfile ? (
              <dl className="kv">
                <dt>Full name</dt>
                <dd>{masterProfile.fullName}</dd>
                <dt>Ghana Card</dt>
                <dd>{masterProfile.ghanaCardNumber || 'Not set'}</dd>
                <dt>Phone</dt>
                <dd>{masterProfile.phone || 'Not set'}</dd>
                <dt>Guardian</dt>
                <dd>{masterProfile.guardianName || 'Not set'}</dd>
                <dt>Previous school</dt>
                <dd>{masterProfile.previousSchool || 'Not set'}</dd>
                <dt>Index number</dt>
                <dd>{masterProfile.indexNumber || 'Not set'}</dd>
                <dt>Aggregate</dt>
                <dd>{masterProfile.aggregate ?? 'Not set'}</dd>
              </dl>
            ) : (
              <Muted>
                You have not completed your master profile yet.{' '}
                <button type="button" className="lk" onClick={() => navigate('/profile')}>
                  Complete it now
                </button>
                , otherwise this application cannot be submitted.
              </Muted>
            )}
          </div>
          <div>
            <p className="hd">What is still missing</p>
            {missingDocuments.length || missingAnswers.length || missingProfile.length ? (
              <ul className="req">
                {missingProfile.map((item) => <li key={`p-${item}`}>Profile: {item}</li>)}
                {missingAnswers.map((label) => <li key={`a-${label}`}>Answer: {label}</li>)}
                {missingDocuments.map((label) => <li key={`d-${label}`}>Upload: {label}</li>)}
              </ul>
            ) : (
              <Muted>Everything this school needs is in place.</Muted>
            )}

            {quote ? (
              <>
                <p className="hd">Cost</p>
                <dl className="kv">
                  <dt>Application fee</dt>
                  <dd>GH{quote.applicationFee ?? 'Not set'}</dd>
                  <dt>Entrance service fee</dt>
                  <dd>GH{quote.serviceFee ?? 0}</dd>
                  <dt>Total</dt>
                  <dd>GH{quote.total ?? 'Not set'}</dd>
                </dl>
              </>
            ) : null}
          </div>
        </Grid2>

        {!complete ? (
          <Banner title="Not ready to send yet">
            Finish the items listed above. Entrance will not let you send a partial application.
          </Banner>
        ) : application.status !== 'draft' ? (
          <Banner title="Already sent">
            {school.name} has this application. Follow it on the Track page.
          </Banner>
        ) : (
          <>
            <p className="mu">
              {school.name} receives your master profile, your answers to its questions and the
              documents it asked for. Nothing is shared with any other school.
            </p>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <Button variant="s" onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? 'Hide preview' : 'Preview'}
              </Button>
            </div>
            <div style={{ height: 10 }} />
            <Button busy={busy === 'submit'} onClick={sendApplication}>
              Send application to {school.shortName}
            </Button>
            <p className="mu" style={{ marginTop: 10 }}>
              The application fee is settled separately. Sending your application does not pay it, and
              the school can start reviewing straight away.
            </p>
          </>
        )}
      </Card>

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>History</h3>
        {(application.history || []).length === 0 ? (
          <Muted>Nothing has happened on this application yet.</Muted>
        ) : (
          (application.history || []).map((entry, i) => (
            <div className="prog" key={`${entry.at}-${i}`}>
              <div style={{ flex: 1 }}>
                <b>{String(entry.to || '').replace(/_/g, ' ')}</b>
                {entry.note ? <div className="mu">{entry.note}</div> : null}
              </div>
              <span className="mu">{fmtDate(entry.at)}</span>
            </div>
          ))
        )}
        <div style={{ marginTop: 12 }}>
          <Button variant="s" size="sm" onClick={onDone}>Close</Button>
        </div>
      </Card>
      {showPreview ? <SchoolPreview applicationId={application.id} onClose={() => setShowPreview(false)} /> : null}
    </>
  );
}

/**
 * Everything the school will read, fetched from the same builder the staff view
 * uses. Deliberately explicit: it shows blanks as "(not set)" rather than hiding
 * a missing field, so nothing is silently withheld.
 */
function SchoolPreview({ applicationId, onClose }) {
  const { data, loading, error } = useAsync(() => applicationApi.preview(applicationId), [applicationId]);

  if (loading) {
    return (
      <Modal title="Preview" onClose={onClose}>
        <Spinner label="Loading what the school receives" />
      </Modal>
    );
  }

  if (error || !data) {
    return (
      <Modal title="Preview unavailable" onClose={onClose}>
        {error?.message || 'We could not build the preview. Your application is unchanged.'}
      </Modal>
    );
  }

  const p = data.preview || {};
  const { applicant, profile, result, programmes, answers, commonDocuments, schoolDocuments, questions, payment } = p;
  const show = (v) => (v === null || v === undefined || v === '' ? <span className="mu">Not set</span> : String(v));

  return (
    <Modal
      title={`What ${p.application?.reference} sends to the school`}
      labelledBy="preview-title"
      onClose={onClose}
      footer={
        <Button variant="s" onClick={onClose}>Close preview</Button>
      }
    >
      <Muted>
        This is the complete record the school's admissions staff can open. Nothing here goes to any
        other school.
      </Muted>

      <p className="hd">Programmes ({programmes?.length || 0})</p>
      {programmes?.length ? programmes.map((x) => (
        <dl className="kv" key={x.id}>
          <dt>{x.name}</dt>
          <dd>
            Cut-off {show(x.cutOff)}{x.cutOffEstimated ? ' (estimated)' : ''}
            {x.ruleText ? ` · ${x.ruleText}` : ''}
            {x.meetsCutOff ? ' · meets cut-off' : ''}
          </dd>
        </dl>
      )) : <p className="mu">No programmes chosen yet.</p>}

      <p className="hd">Your details</p>
      <dl className="kv">
        <dt>Name on account</dt><dd>{show(applicant?.name)}</dd>
        <dt>Email</dt><dd>{show(applicant?.email)}{applicant?.emailVerified ? ' (verified)' : ' (not verified)'}</dd>
        <dt>Phone</dt><dd>{show(applicant?.phone)}</dd>
      </dl>

      <p className="hd">Master profile</p>
      <dl className="kv">
        <dt>Full name</dt><dd>{show(profile?.fullName)}</dd>
        <dt>Date of birth</dt><dd>{profile?.dateOfBirth ? fmtDate(profile.dateOfBirth) : <span className="mu">Not set</span>}</dd>
        <dt>Gender</dt><dd>{show(profile?.gender)}</dd>
        <dt>Nationality</dt><dd>{show(profile?.nationality)}</dd>
        <dt>Ghana Card number</dt><dd>{show(profile?.ghanaCardNumber)}</dd>
        <dt>Special needs</dt><dd>{show(profile?.specialNeeds)}</dd>
        <dt>Region</dt><dd>{show(profile?.region)}</dd>
        <dt>Hometown</dt><dd>{show(profile?.homeTown)}</dd>
        <dt>Guardian</dt><dd>{show(profile?.guardianName)}</dd>
        <dt>Guardian phone</dt><dd>{show(profile?.guardianPhone)}</dd>
        <dt>Previous school</dt><dd>{show(profile?.previousSchool)}</dd>
        <dt>Exam</dt><dd>{show(profile?.exam)}</dd>
        <dt>Exam year</dt><dd>{show(profile?.examYear)}</dd>
        <dt>Index number</dt><dd>{show(profile?.indexNumber)}</dd>
      </dl>

      <p className="hd">WASSCE result</p>
      {result ? (
        <dl className="kv">
          <dt>Aggregate</dt><dd>{show(result.aggregate)}</dd>
          <dt>English</dt><dd>{show(result.eng)}</dd>
          <dt>Mathematics</dt><dd>{show(result.math)}</dd>
          <dt>Science</dt><dd>{show(result.sci)}</dd>
          <dt>Social</dt><dd>{show(result.soc)}</dd>
          {result.electives?.map((e) => (
            <span key={e.code} style={{ display: 'contents' }}>
              <dt>{e.subject || e.code}</dt><dd>{show(e.grade)}</dd>
            </span>
          ))}
        </dl>
      ) : <p className="mu">No result added, so the school sees no grades.</p>}

      <p className="hd">Documents ({Object.keys(commonDocuments || {}).length + (schoolDocuments?.length || 0)})</p>
      {Object.keys(commonDocuments || {}).length + (schoolDocuments?.length || 0) ? (
        <ul>
          {Object.entries(commonDocuments || {}).map(([key, d]) => (
            <li key={key}>{COMMON_DOCUMENT_LABELS[key] || key}: {d.originalName}</li>
          ))}
          {(schoolDocuments || []).map((d) => (
            <li key={d.id}>{d.kind}: {d.originalName}</li>
          ))}
        </ul>
      ) : <p className="mu">No documents attached.</p>}

      <p className="hd">Questions the school asked ({questions?.length || 0})</p>
      {questions?.length ? (
        <dl className="kv">
          {questions.map((q) => {
            const given = answers?.find((a) => a.key === q.key)?.value;
            return (
              <span key={q.key} style={{ display: 'contents' }}>
                <dt>{q.label}</dt>
                <dd>{given ? String(given) : <span className="mu">Not answered</span>}</dd>
              </span>
            );
          })}
        </dl>
      ) : <p className="mu">This school asked no questions.</p>}

      <p className="hd">Payment</p>
      <dl className="kv">
        <dt>Status</dt><dd>{show(payment?.status)}</dd>
        <dt>Application fee</dt><dd>{show(payment?.applicationFee)}</dd>
        <dt>Entrance service fee</dt><dd>{show(payment?.serviceFee)}</dd>
        <dt>Total</dt><dd>{show(payment?.total)}</dd>
      </dl>
    </Modal>
  );
}
